import { networkService } from '@/services/network.service';
import OfflineLogger from '@/utils/offlineLogger';
import { SyncRunner, syncRunner } from './sync-runner';

export interface SyncTask {
  id: string;
  entityName: string;
  userId: string;
  priority: number; // 1 = highest, 5 = lowest
  operation: 'full-sync' | 'incremental' | 'validation';
  retryCount: number;
  maxRetries: number;
  timestamp: number;
  timeout: number; // ms
  timeoutFailures: number;
}

export interface SyncQueueStatus {
  isProcessing: boolean;
  currentTask: SyncTask | null;
  queueLength: number;
  completedTasks: number;
  failedTasks: number;
}

// Chiavi localStorage: questa coda gestisce *download* idempotenti dell'initial
// sync (full-sync/incremental/validation). La persistiamo qui — non nella coda
// Dexie delle mutazioni, che ha semantica diversa (modifiche in uscita) — così
// retry e fallimenti sopravvivono alla chiusura dell'app invece di sparire dai
// soli `setTimeout`/contatori in memoria. Vedi issue #1423.
const PENDING_KEY = 'sync_queue_pending';
const FAILED_KEY = 'sync_queue_failed';
const MAX_FAILED_TRACE = 50;

export class SyncQueueManager {
  private queue: SyncTask[] = [];
  private isProcessing = false;
  private currentTask: SyncTask | null = null;
  private completedTasks = 0;
  private failedTasks = 0;
  private processLock = false;
  private readonly maxConcurrentTasks = 1; // Evita sync paralleli
  private currentTaskPromise: Promise<{ success: boolean; timedOut: boolean }> | null = null;
  // Task in attesa di un retry schedulato: non sono in `this.queue` durante il
  // delay, quindi vanno tracciati a parte per poterli persistere.
  private scheduledRetries: SyncTask[] = [];

  constructor(private runner: SyncRunner = syncRunner) {}

  async addTask(
    entityName: string,
    userId: string,
    operation: 'full-sync' | 'incremental' | 'validation',
    priority: number = 3,
    timeout: number = 30000
  ): Promise<string> {
    const taskId = `${entityName}-${operation}-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    if (!networkService.isOnline()) {
      OfflineLogger.warn(
        'SyncQueueManager',
        `Network offline, task ${taskId} not queued`
      );
      return taskId;
    }

    const task: SyncTask = {
      id: taskId,
      entityName,
      userId,
      priority,
      operation,
      retryCount: 0,
      maxRetries: operation === 'validation' ? 1 : 3,
      timestamp: Date.now(),
      timeout,
      timeoutFailures: 0
    };

    const existingTaskIndex = this.queue.findIndex(
      t => t.entityName === entityName && t.operation === operation && t.userId === userId
    );

    if (existingTaskIndex !== -1) {
      OfflineLogger.info('SyncQueueManager', `Replacing existing task for ${entityName}-${operation}`);
      this.queue[existingTaskIndex] = task;
    } else {
      this.queue.push(task);
      OfflineLogger.info('SyncQueueManager', `Added task ${taskId} to queue (priority: ${priority})`);
    }

    this.queue.sort((a, b) => a.priority - b.priority);
    this.persistState();

    if (!this.isProcessing) {
      setTimeout(() => this.processQueue(), 100);
    }

    return taskId;
  }

  private async processQueue(): Promise<void> {
    if (this.processLock || this.isProcessing || this.queue.length === 0) {
      return;
    }

    this.processLock = true;
    this.isProcessing = true;

    OfflineLogger.info('SyncQueueManager', `Starting queue processing. Queue length: ${this.queue.length}`);

    while (this.queue.length > 0 && networkService.isOnline()) {
      const task = this.queue.shift()!;
      this.currentTask = task;
      this.persistState();

      try {
        OfflineLogger.info(
          'SyncQueueManager',
          `Processing task ${task.id} (${task.entityName}-${task.operation}) for user ${task.userId}`
        );

        this.currentTaskPromise = this.runner.runWithTimeout(task);
        const { success, timedOut } = await this.currentTaskPromise;
        this.currentTaskPromise = null;

        if (success) {
          this.completedTasks++;
          OfflineLogger.info('SyncQueueManager', `✅ Task ${task.id} completed successfully`);
        } else {
          await this.handleTaskFailure(task, timedOut);
        }
      } catch (error) {
        OfflineLogger.error('SyncQueueManager', `Task ${task.id} failed: ${error}`);
        await this.handleTaskFailure(task, false);
      }

      this.currentTask = null;
      this.persistState();

      await new Promise(resolve => setTimeout(resolve, 200));
    }

    this.isProcessing = false;
    this.processLock = false;

    OfflineLogger.info(
      'SyncQueueManager',
      `Queue processing completed. Completed: ${this.completedTasks}, Failed: ${this.failedTasks}`
    );
  }

  private async handleTaskFailure(task: SyncTask, timedOut = false): Promise<void> {
    task.retryCount++;

    if (timedOut) {
      task.timeoutFailures++;
      if (task.timeoutFailures >= 2) {
        task.maxRetries = Math.min(task.maxRetries, task.retryCount + 1);
      }
    } else {
      task.timeoutFailures = 0;
    }

    if (task.retryCount <= task.maxRetries) {
      const delay = Math.min(1000 * Math.pow(2, task.retryCount - 1), 10000);

      OfflineLogger.warn(
        'SyncQueueManager',
        `Task ${task.id} failed, retrying in ${delay}ms (attempt ${task.retryCount}/${task.maxRetries})`
      );

      // Il task resta persistito come pending durante il delay: se l'app viene
      // chiusa ora, `restorePersistedTasks` lo riprende al riavvio.
      this.scheduledRetries.push(task);
      this.persistState();

      setTimeout(() => {
        this.scheduledRetries = this.scheduledRetries.filter(t => t.id !== task.id);
        task.timestamp = Date.now();

        // Check di rete prima di ri-accodare: se nel frattempo siamo offline non
        // bruciamo il tentativo e non avviamo un processing che fallirebbe subito.
        // Il task resta comunque in coda e persistito, pronto al prossimo trigger.
        if (!networkService.isOnline()) {
          OfflineLogger.warn(
            'SyncQueueManager',
            `Task ${task.id} retry posticipato: rete offline, resta in coda`
          );
          this.queue.unshift(task);
          this.persistState();
          return;
        }

        this.queue.unshift(task);
        this.persistState();
        if (!this.isProcessing) {
          this.processQueue();
        }
      }, delay);
    } else {
      this.failedTasks++;
      // Traccia diagnosticabile del fallimento definitivo (parità con la coda
      // Dexie delle mutazioni: `resetFailedOperations`/badge "tocca per riprovare").
      this.recordFailedTask(task);
      OfflineLogger.error(
        'SyncQueueManager',
        `Task ${task.id} failed permanently after ${task.maxRetries} retries`
      );
    }
  }

  getQueueStatus(): SyncQueueStatus {
    return {
      isProcessing: this.isProcessing,
      currentTask: this.currentTask,
      queueLength: this.queue.length,
      completedTasks: this.completedTasks,
      failedTasks: this.failedTasks
    };
  }

  clearQueue(): void {
    const activeTask = this.currentTask;
    this.queue = [];
    this.scheduledRetries = [];
    this.isProcessing = false;
    this.currentTask = null;
    this.clearPersistedPending();

    if (activeTask) {
      OfflineLogger.warn(
        'SyncQueueManager',
        `Queue cleared. Queue length: ${this.queue.length}. Active task still running: ${activeTask.id} for user ${activeTask.userId}`
      );
    } else {
      OfflineLogger.info(
        'SyncQueueManager',
        `Queue cleared. Queue length: ${this.queue.length}. No active tasks`
      );
    }
  }

  async cancelAll(): Promise<void> {
    this.runner.currentAbortController?.abort();
    this.queue = [];
    this.scheduledRetries = [];
    if (this.currentTaskPromise) {
      try {
        await this.currentTaskPromise;
      } catch (error) {
        OfflineLogger.error('SyncQueueManager', `Error waiting current task: ${error}`);
      }
      this.currentTaskPromise = null;
    }
    this.isProcessing = false;
    this.currentTask = null;
    this.processLock = false;
    // NB: non tocchiamo la persistenza qui. `cancelAll` serve anche all'init di
    // un nuovo utente (useInitialSync): azzerare il localStorage cancellerebbe i
    // task da ripristinare dopo un riavvio. La pulizia durevole avviene solo al
    // logout, via `clearPersisted()`.
    OfflineLogger.warn('SyncQueueManager', 'All sync tasks cancelled');
  }

  async waitForCompletion(maxWaitTime: number = 60000): Promise<boolean> {
    const startTime = Date.now();

    return new Promise(resolve => {
      const checkInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;

        if (!this.isProcessing && this.queue.length === 0) {
          clearInterval(checkInterval);
          resolve(true);
        } else if (elapsed >= maxWaitTime) {
          clearInterval(checkInterval);
          OfflineLogger.warn('SyncQueueManager', `Wait for completion timed out after ${maxWaitTime}ms`);
          resolve(false);
        }
      }, 500);
    });
  }

  /**
   * Ricarica dal localStorage i task pending dell'utente rimasti in sospeso da
   * una sessione precedente (es. app chiusa durante il delay di un retry) e
   * riprende il processing se online. La logica `replace` di `addTask` evita
   * doppioni con un eventuale nuovo initial sync. Ritorna il numero ripristinato.
   */
  restorePersistedTasks(userId: string): number {
    const persisted = this.readPersisted(PENDING_KEY);
    if (!persisted.length) {
      return 0;
    }

    const restorable = persisted.filter(
      t => t.userId === userId && t.retryCount <= t.maxRetries
    );

    let restored = 0;
    for (const task of restorable) {
      const exists = this.queue.some(
        t =>
          t.entityName === task.entityName &&
          t.operation === task.operation &&
          t.userId === task.userId
      );
      const isCurrent =
        this.currentTask?.entityName === task.entityName &&
        this.currentTask?.operation === task.operation &&
        this.currentTask?.userId === task.userId;

      if (!exists && !isCurrent) {
        this.queue.push(task);
        restored++;
      }
    }

    if (restored === 0) {
      return 0;
    }

    this.queue.sort((a, b) => a.priority - b.priority);
    this.persistState();

    OfflineLogger.info(
      'SyncQueueManager',
      `Ripristinati ${restored} task pending da sessione precedente`
    );

    if (!this.isProcessing && networkService.isOnline()) {
      setTimeout(() => this.processQueue(), 100);
    }

    return restored;
  }

  /** Task falliti definitivamente, traccia diagnosticabile persistita. */
  getFailedTasks(): SyncTask[] {
    return this.readPersisted(FAILED_KEY);
  }

  /**
   * Ri-accoda i task falliti definitivamente come pending (retry azzerato) e
   * riprende il processing se online. Parità con `resetFailedOperations` della
   * coda mutazioni. Ritorna il numero di task reimmessi.
   */
  resetFailedTasks(userId?: string): number {
    const failed = this.readPersisted(FAILED_KEY);
    if (!failed.length) {
      return 0;
    }

    const toReset = userId ? failed.filter(t => t.userId === userId) : failed;
    const remaining = userId ? failed.filter(t => t.userId !== userId) : [];

    if (!toReset.length) {
      return 0;
    }

    for (const task of toReset) {
      task.retryCount = 0;
      task.timeoutFailures = 0;
      task.timestamp = Date.now();
      this.queue.push(task);
    }

    this.writePersisted(FAILED_KEY, remaining);
    this.queue.sort((a, b) => a.priority - b.priority);
    this.persistState();

    OfflineLogger.info(
      'SyncQueueManager',
      `Reimmessi ${toReset.length} task falliti per nuovo tentativo`
    );

    if (!this.isProcessing && networkService.isOnline()) {
      setTimeout(() => this.processQueue(), 100);
    }

    return toReset.length;
  }

  /** Scarta la traccia dei task falliti senza ritentarli. */
  clearFailedTasks(): void {
    this.writePersisted(FAILED_KEY, []);
  }

  /** Pulizia durevole completa (pending + falliti). Da usare al logout. */
  clearPersisted(): void {
    this.clearPersistedPending();
    this.writePersisted(FAILED_KEY, []);
  }

  // --- Persistenza interna -------------------------------------------------

  private persistState(): void {
    // Unione di tutto ciò che è ancora "in volo": coda, task corrente e retry
    // schedulati. Dedup per entità+operazione+utente (l'ultimo vince).
    const all = [
      ...this.queue,
      ...(this.currentTask ? [this.currentTask] : []),
      ...this.scheduledRetries
    ];

    const byKey = new Map<string, SyncTask>();
    for (const t of all) {
      byKey.set(`${t.userId}|${t.entityName}|${t.operation}`, t);
    }

    const snapshot = [...byKey.values()];
    if (snapshot.length === 0) {
      this.clearPersistedPending();
      return;
    }
    this.writePersisted(PENDING_KEY, snapshot);
  }

  private recordFailedTask(task: SyncTask): void {
    const failed = this.readPersisted(FAILED_KEY);
    failed.push(task);
    // Limita la dimensione della traccia per non far crescere localStorage.
    const trimmed = failed.slice(-MAX_FAILED_TRACE);
    this.writePersisted(FAILED_KEY, trimmed);
  }

  private clearPersistedPending(): void {
    this.writePersisted(PENDING_KEY, []);
  }

  private readPersisted(key: string): SyncTask[] {
    try {
      if (typeof localStorage === 'undefined') return [];
      const raw = localStorage.getItem(key);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as SyncTask[]) : [];
    } catch (error) {
      OfflineLogger.error('SyncQueueManager.readPersisted', error);
      return [];
    }
  }

  private writePersisted(key: string, tasks: SyncTask[]): void {
    try {
      if (typeof localStorage === 'undefined') return;
      if (tasks.length === 0) {
        localStorage.removeItem(key);
        return;
      }
      localStorage.setItem(key, JSON.stringify(tasks));
    } catch (error) {
      OfflineLogger.error('SyncQueueManager.writePersisted', error);
    }
  }
}

export const syncQueueService = new SyncQueueManager();
